/**
 * Coups de pouce — Attendu 656 « Cercle et disque comme ensembles de points ».
 *
 * Une entrée par `generator_key` (cf. gen_656.js). Contrat d'une entrée :
 *   pages : 2 pages MAXIMUM, chacune { titre, lignes?, svg? }.
 *           Page 1 « La règle »   : les phrases du mini-cours.
 *           Page 2 « Un exemple » : la figure qui les illustre.
 *
 * Règle absolue : l'aide porte sur d'AUTRES lettres et d'AUTRES valeurs que
 * celles que le générateur peut tirer, sinon elle donne la réponse. Chaque
 * coup de pouce est autonome — les formats sont indépendants et un exercice
 * peut ne jamais être donné.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure Ex. 1 : les trois positions d'un coup ──
// r = 10 cm : le générateur tire r ∈ [2..8], donc hors de portée. Les trois cas
// sont montrés ENSEMBLE parce que c'est leur comparaison qui fait la notion :
// séparés, chacun paraît évident ; réunis, on voit que seule la distance décide.
// Centre nommé A et points B, C, D : mêmes lettres que la règle, pour que
// l'élève reconnaisse la convention au lieu d'avoir à la retraduire.
const SVG_TROIS_POSITIONS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 212" role="img"
     aria-label="Un cercle de centre A et de rayon 10 cm, avec B intérieur à 6 cm, C sur le cercle à 10 cm et D extérieur à 14 cm">
  <circle cx="100" cy="100" r="58" stroke="#0f172a" fill="none" stroke-width="2"/>
  <line x1="100" y1="100" x2="133" y2="53" stroke="#F97316" stroke-width="2.5"/>
  <circle cx="100" cy="100" r="3" fill="#0f172a"/>
  <text x="92" y="112" text-anchor="end" font-size="12.5" fill="#0f172a">A</text>
  <text x="128" y="82" font-size="11" font-weight="700" fill="#F97316">r</text>

  <circle cx="67" cy="88" r="4" fill="#378ADD"/>
  <text x="60" y="82" text-anchor="end" font-size="12.5" font-weight="700" fill="#378ADD">B</text>
  <circle cx="133" cy="53" r="4" fill="#F97316"/>
  <text x="140" y="47" font-size="12.5" font-weight="700" fill="#F97316">C</text>
  <circle cx="167" cy="147" r="4" fill="#E24B4A"/>
  <text x="174" y="157" font-size="12.5" font-weight="700" fill="#E24B4A">D</text>

  <circle cx="196" cy="62" r="4" fill="#378ADD"/>
  <text x="208" y="66" font-size="11.5" fill="#334155">AB = 6 cm &lt; r  →  intérieur</text>
  <circle cx="196" cy="97" r="4" fill="#F97316"/>
  <text x="208" y="101" font-size="11.5" fill="#334155">AC = 10 cm = r  →  sur le cercle</text>
  <circle cx="196" cy="132" r="4" fill="#E24B4A"/>
  <text x="208" y="136" font-size="11.5" fill="#334155">AD = 14 cm &gt; r  →  extérieur</text>

  <text x="190" y="192" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">ici r = 10 cm</text>
</svg>`.trim();

// ── Figure Ex. 3 : la carte des quatre zones ──
// AUCUN point nommé, volontairement : l'exercice demande « quel point est dans
// telle zone », et une figure qui placerait des points étiquetés répondrait
// d'avance. Ici on montre la GÉOGRAPHIE — les quatre régions que découpent deux
// disques sécants — et l'élève y situe lui-même ses propres points.
const SVG_QUATRE_ZONES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 218" role="img"
     aria-label="Deux disques sécants découpant quatre zones : dans le violet seulement, dans les deux, dans l'orange seulement, dans aucun des deux">
  <circle cx="130" cy="88" r="58" fill="#9333EA" fill-opacity="0.12" stroke="#9333EA" stroke-width="2"/>
  <circle cx="205" cy="88" r="58" fill="#F97316" fill-opacity="0.12" stroke="#F97316" stroke-width="2"/>

  <circle cx="98" cy="88" r="11" fill="#ffffff" stroke="#9333EA" stroke-width="1.5"/>
  <text x="98" y="93" text-anchor="middle" font-size="12" font-weight="700" fill="#9333EA">1</text>
  <circle cx="167" cy="88" r="11" fill="#ffffff" stroke="#0f172a" stroke-width="1.5"/>
  <text x="167" y="93" text-anchor="middle" font-size="12" font-weight="700" fill="#0f172a">2</text>
  <circle cx="238" cy="88" r="11" fill="#ffffff" stroke="#F97316" stroke-width="1.5"/>
  <text x="238" y="93" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">3</text>
  <circle cx="330" cy="46" r="11" fill="#ffffff" stroke="#64748b" stroke-width="1.5"/>
  <text x="330" y="51" text-anchor="middle" font-size="12" font-weight="700" fill="#64748b">4</text>

  <g font-size="11.5" fill="#334155">
    <text x="24" y="182">1  dans le violet seulement</text>
    <text x="206" y="182">3  dans l'orange seulement</text>
    <text x="24" y="206">2  dans les deux</text>
    <text x="206" y="206">4  dans aucun des deux</text>
  </g>
</svg>`.trim();

// ── Figure Ex. 4 : chaque formulation désigne une zone ──
// AUCUN comptage, volontairement : l'exercice demande « combien de points… », et
// une figure qui en compterait donnerait la méthode ET le résultat. Ici on
// étiquette les trois zones avec les formulations EXACTES des questions, ce qui
// est précisément le point où l'élève se trompe — « appartient au cercle » n'est
// pas « est dans le disque ».
const SVG_ZONES_VOCABULAIRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 190" role="img"
     aria-label="Un cercle avec un point à l'intérieur, un point sur le trait et un point à l'extérieur, chacun relié à la formulation qui le désigne">
  <circle cx="110" cy="100" r="62" stroke="#0f172a" fill="none" stroke-width="2"/>
  <circle cx="110" cy="100" r="3" fill="#0f172a"/>
  <line x1="110" y1="100" x2="157" y2="60" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="3 3"/>
  <text x="128" y="72" font-size="11" font-weight="700" fill="#64748b">r</text>

  <circle cx="85" cy="118" r="4.5" fill="#378ADD"/>
  <circle cx="157" cy="60" r="4.5" fill="#F97316"/>
  <circle cx="170" cy="168" r="4.5" fill="#E24B4A"/>

  <circle cx="206" cy="66" r="4.5" fill="#378ADD"/>
  <text x="218" y="70" font-size="11.5" fill="#334155">à moins de r cm</text>
  <circle cx="206" cy="101" r="4.5" fill="#F97316"/>
  <text x="218" y="105" font-size="11.5" fill="#334155">appartient au cercle</text>
  <circle cx="206" cy="136" r="4.5" fill="#E24B4A"/>
  <text x="218" y="140" font-size="11.5" fill="#334155">à plus de r cm</text>
</svg>`.trim();

// ── Figure Ex. 5 : le geste réel, cliquer-glisser (ANIMÉE) ──
// PAS de compas : l'outil de construction n'en propose aucun. Son propre message
// dit « Clique sur le centre dans la figure, puis glisse pour le rayon. » — une
// aide qui parlerait de reporter un écartement enverrait l'élève chercher un
// bouton qui n'existe pas. On montre donc le tirage depuis le centre, et le
// segment de référence posé juste au-dessus pour comparer les longueurs.
// Le rayon tracé et le segment KL font exactement la même longueur en pixels.
//
// Animation SMIL (même technique que CardClasse.jsx) : le rayon croît de 0 à la
// longueur de [KL], marque un temps, puis recommence. Les valeurs STATIQUES des
// attributs sont celles de l'état final (r = 70, x2 = 192) : si SMIL est
// indisponible ou l'animation coupée, la figure reste complète et juste.
// ⚠ SMIL ne respecte pas prefers-reduced-motion — limitation partagée avec les
// autres animations SVG du projet.
const SVG_RAYON_REPORTE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 210" role="img"
     aria-label="Le segment KL mesure 70 pixels ; on clique sur le centre S et on glisse jusqu'à obtenir un rayon de même longueur">
  <text x="190" y="16" text-anchor="middle" font-size="12" fill="#64748b">« l'ensemble des points à la distance KL du point S »</text>

  <line x1="130" y1="42" x2="200" y2="42" stroke="#F97316" stroke-width="3.5"/>
  <line x1="130" y1="36" x2="130" y2="48" stroke="#F97316" stroke-width="2"/>
  <line x1="200" y1="36" x2="200" y2="48" stroke="#F97316" stroke-width="2"/>
  <text x="130" y="30" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">K</text>
  <text x="200" y="30" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">L</text>

  <line x1="130" y1="50" x2="130" y2="118" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3 3"/>
  <line x1="200" y1="50" x2="200" y2="118" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3 3"/>

  <circle cx="130" cy="126" r="70" stroke="#1D9E75" fill="none" stroke-width="2.5" stroke-dasharray="5 4">
    <animate attributeName="r" values="0;0;70;70" keyTimes="0;0.12;0.62;1"
             dur="3.4s" repeatCount="indefinite"/>
  </circle>
  <circle cx="130" cy="126" r="4.5" fill="#0f172a"/>
  <text x="122" y="140" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">S</text>

  <line x1="130" y1="126" x2="192" y2="126" stroke="#F97316" stroke-width="3.5">
    <animate attributeName="x2" values="130;130;192;192" keyTimes="0;0.12;0.62;1"
             dur="3.4s" repeatCount="indefinite"/>
  </line>
  <g>
    <polygon points="200,126 188,121 188,131" fill="#F97316"/>
    <animateTransform attributeName="transform" type="translate"
                      values="-70,0;-70,0;0,0;0,0" keyTimes="0;0.12;0.62;1"
                      dur="3.4s" repeatCount="indefinite"/>
  </g>
  <text x="165" y="118" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">glisse</text>

  <text x="214" y="132" font-size="11.5" font-weight="700" fill="#1D9E75">même longueur</text>
  <text x="214" y="148" font-size="11.5" fill="#64748b">que [KL]</text>
</svg>`.trim();

const CP_656 = {
    position_point_qcm_ensemble: {
        pages: [
            {
                titre: 'La règle',
                // Convention posée en ligne 1 : le générateur tire ses lettres au
                // hasard, l'élève doit donc savoir à quoi A et B correspondent
                // pour transposer. L'exemple emploie les mêmes lettres.
                lignes: [
                    'Le cercle a pour centre **A** et pour rayon **r** ; **B** est le point à situer.',
                    '**AB < r** → B est à l\'**intérieur** du disque.',
                    '**AB = r** → B est **sur** le cercle.',
                    '**AB > r** → B est à l\'**extérieur** du disque.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_TROIS_POSITIONS },
        ],
    },

    classement_points_vf_figure: {
        pages: [
            {
                titre: 'La règle',
                // Sans lettres : ce format lit une figure, les noms des points
                // sont tirés au hasard. La ligne 3 mentionne le centre parce
                // qu'il revient dans le pool et que c'est le cas le moins intuitif.
                lignes: [
                    'Le **cercle**, c\'est le trait seul. Le **disque**, c\'est le trait **et** tout l\'intérieur.',
                    'Un point **sur le trait** appartient donc **aux deux**.',
                    'Un point **à l\'intérieur** appartient au disque seulement, le centre aussi.',
                    'Un point **à l\'extérieur** n\'appartient ni à l\'un ni à l\'autre.',
                ],
            },
            // Pas de page « Un exemple » ici : les quatre lignes couvrent les
            // quatre positions possibles, il n'en reste aucune à illustrer. Une
            // figure ne ferait que rejouer les affirmations de l'exercice.
        ],
    },

    zone_double_qcm_figure: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Regarde chaque disque **l\'un après l\'autre** : le point est-il dedans, ou dehors ?',
                    'Le bon point est celui qui remplit **les deux conditions** de la question.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_QUATRE_ZONES },
        ],
    },

    compter_points_zone_saisie: {
        pages: [
            {
                titre: 'La règle',
                // La ligne 2 fait la correspondance entre les trois formulations
                // de question et les trois zones : c'est là que l'élève se
                // trompe. Pas de troisième ligne « compte un par un » : un
                // méta-conseil n'enseigne rien de mathématique.
                lignes: [
                    'Le trait du cercle sépare l\'**intérieur** de l\'**extérieur**.',
                    '« À moins de r cm » = **dedans**. « À plus de r cm » = **dehors**. « Appartient au cercle » = **sur le trait**.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_ZONES_VOCABULAIRE },
        ],
    },

    ensemble_points_programme: {
        pages: [
            {
                titre: 'La règle',
                // Les trois consignes du générateur donnent le rayon de deux
                // façons : par un nombre (« à 3 cm de P1 ») ou par deux points
                // (« BM = BC », « à la distance DE »). Les lignes 2 et 3
                // traitent ces deux cas séparément — c'est le second qui bloque,
                // parce qu'aucune mesure n'y est écrite.
                lignes: [
                    'L\'ensemble des points à une même distance d\'un point, c\'est un **cercle** : ce point est le **centre**, la distance est le **rayon**.',
                    'Rayon donné par une **longueur** (3 cm) : ouvre le compas à cette mesure sur ta règle.',
                    'Rayon donné par **deux points** (JM = JR, ou la distance ZC) : pique le compas sur l\'un, ouvre jusqu\'à l\'autre. Tu as le rayon sans le mesurer.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_RAYON_REPORTE },
        ],
    },
};

export default CP_656;
